import _ from 'lodash-es';
import React, { useState, useMemo, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import {
  Typography,
  Modal,
  Box,
  Button,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  TextField,
  Stack,
} from '@mui/material';

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { useConfirm } from 'material-ui-confirm';
import { CheckCircleOutline, Delete } from '@mui/icons-material';
import TemplatesApi from './Templates.api';
import TemplateContentForm from './TemplateContentForm';
import styleColors from '../colors.styles';
import { AlertsContext } from '../shared/alerts/Alerts.context';
import SpinningSpark from '../shared/components/SpinningSpark';

const SHOPPABLE_CATEGORY_ID = 'pl';
const SHOPPABLE_TOPIC_NAME = 'product links';

const TEMPLATE_CATEGORIES = [
  { id: 'pw', name: 'Pre-written' },
  { id: 'fitb', name: 'Fill-in-the-Blank' },
  { id: SHOPPABLE_CATEGORY_ID, name: 'Product Links' },
];
const DEFAULT_TEMPLATE = {
  message: '',
  topic: '',
  subtopic: '',
  source: null,
  type: 'text',
  category: 'pw',
  created_source: 'desktop',
  link: '',
  cta: '',
  title: '',
  description: '',
};

const DEFAULT_TOPIC = {
  name: '',
  category: 'template',
};

const getInitialDate = (date) => {
  return date ? moment.tz(date, 'America/Chicago') : null;
};

export default function TemplateCreateModal({
  topics,
  onCreate,
  closeModal,
  initialTemplate,
}) {
  const [template, setTemplate] = useState({
    ...DEFAULT_TEMPLATE,
    ...initialTemplate,
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [evergreen, setEvergreen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [addTopic, setAddTopic] = useState(false);
  const [addSubtopic, setAddSubtopic] = useState(false);
  const [newTopic, setNewTopic] = useState('');
  const [newSubtopic, setNewSubtopic] = useState('');
  const [localTopics, setLocalTopics] = useState(topics);
  const [topicLoading, setTopicLoading] = useState(false);
  const [subtopicLoading, setSubtopicLoading] = useState(false);

  const confirm = useConfirm();
  const { showAlert } = useContext(AlertsContext);
  const topicsMap = useMemo(() => _.keyBy(localTopics, 'id'), [localTopics]);
  const subtopics = topicsMap[template.topic]?.subtopics ?? [];
  const isShoppableSelected = template.category === SHOPPABLE_CATEGORY_ID;

  useEffect(() => {
    if (initialTemplate) {
      setStartDate(getInitialDate(initialTemplate.start_date));
      setEndDate(getInitialDate(initialTemplate.end_date));
      setEvergreen(initialTemplate.end_date === null);
    }
  }, [initialTemplate]);

  const validate = () => {
    const newErrors = {};
    const requiredFields = ['category', 'message'];

    if (isShoppableSelected) {
      if (!template.link) {
        newErrors.link = 'Product URL is required for Product Link templates';
      } else {
        try {
          // Need to disable ESLint error about new keyword for URL
          // eslint-disable-next-line no-new
          new URL(template.link);
        } catch (error) {
          newErrors.link =
            'Please enter a valid Product URL (e.g., https://www.example.com)';
        }
      }
      if (!template.title) {
        newErrors.title =
          'Product Title is required for Product Link templates';
      }
      if (!template.description) {
        newErrors.description =
          'Product Description is required for Product Link templates';
      }
    } else {
      requiredFields.push('topic', 'subtopic');
    }

    requiredFields.forEach((key) => {
      if (!template[key] && !newErrors[key]) {
        newErrors[key] = 'This field is required';
      }
    });

    if (
      template.category === 'fitb' &&
      template.message.search(/\{.+\}/g) === -1
    ) {
      newErrors.message =
        'Fill-in-the-Blank templates require at least one blank';
    }

    if (!startDate) {
      newErrors.startDate = 'This field is required';
    } else if (errors.startDate) {
      newErrors.startDate = errors.startDate;
    }

    if (!evergreen) {
      if (!endDate) {
        newErrors.endDate = 'This field is required';
      } else if (errors.endDate) {
        newErrors.endDate = errors.endDate;
      } else {
        const timeDiff = endDate.diff(startDate, 'hours');
        if (timeDiff <= 0) {
          newErrors.endDate = 'End date should be after start date';
        }
      }
    }

    setErrors(newErrors);

    return _.isEmpty(newErrors);
  };

  const handleCategoryChange = (e) => {
    const newCategory = e.target.value;
    setTemplate((t) => {
      let updatedT = {
        ...DEFAULT_TEMPLATE,
        ...t,
        category: newCategory,
        link: newCategory === SHOPPABLE_CATEGORY_ID ? t.link : '',
        topic: newCategory === SHOPPABLE_CATEGORY_ID ? '' : t.topic,
        subtopic: newCategory === SHOPPABLE_CATEGORY_ID ? '' : t.subtopic,
      };
      if (newCategory === SHOPPABLE_CATEGORY_ID && t.type === 'video') {
        updatedT = { ...updatedT, source: null, type: 'text' };
      }
      return updatedT;
    });
    if (newCategory === SHOPPABLE_CATEGORY_ID) {
      setErrors((prev) => ({
        ...prev,
        topic: undefined,
        subtopic: undefined,
        link: prev.link,
        source: prev.source,
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        link: undefined,
        source: undefined,
        topic: prev.topic,
        subtopic: prev.subtopic,
      }));
    }
  };

  const onSubmit = async () => {
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const templatePayload = {
        ...template,
        start_date: startDate.format(),
        end_date: evergreen ? null : endDate.format(),
      };

      if (isShoppableSelected) {
        templatePayload.cta = 'SHOP_NOW';
        const shoppableTopic = localTopics.find(
          (t) => t.name.toLowerCase() === SHOPPABLE_TOPIC_NAME
        );
        if (!shoppableTopic) {
          showAlert(
            'Cannot create Product Link template: "Product Links" topic is missing.',
            'error'
          );
          setLoading(false);
          return;
        }

        templatePayload.topic = shoppableTopic?.id;
        const shoppableSubtopic = shoppableTopic.subtopics?.find(
          (subt) => subt.name.toLowerCase() === SHOPPABLE_TOPIC_NAME
        );
        if (!shoppableSubtopic) {
          showAlert(
            'Cannot create Product Link template: "Product Links" subtopic is missing.',
            'error'
          );
          setLoading(false);
          return;
        }
        templatePayload.subtopic = shoppableSubtopic?.id || null;
        templatePayload.type = 'text'; // Product Link templates are always text type
      }

      if (initialTemplate?.id) {
        await TemplatesApi.editTemplate(initialTemplate.id, templatePayload);
      } else {
        await TemplatesApi.createTemplate(templatePayload);
      }

      showAlert(
        `Template ${initialTemplate?.id ? 'updated' : 'created'} successfully!`,
        'success'
      );
      closeModal();
      onCreate();
    } catch (err) {
      console.error(err);
      showAlert(
        `Failed to ${initialTemplate?.id ? 'update' : 'create'} Template`,
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTopic = async () => {
    const trimmedNewTopicName = newTopic.trim().toLowerCase();
    if (!trimmedNewTopicName) return;

    const existing = localTopics.find(
      (t) => t.name.trim().toLowerCase() === trimmedNewTopicName
    );

    if (existing) {
      setTemplate((prev) => ({
        ...prev,
        topic: existing.id,
        subtopic: '',
      }));
      setAddTopic(false);
      setNewTopic('');
      return;
    }

    setTopicLoading(true);
    try {
      const topicPayload = {
        ...DEFAULT_TOPIC,
        name: newTopic,
      };
      const response = await TemplatesApi.createTopic(topicPayload);
      const createdTopic = response.data.data[0];
      setLocalTopics((prevTopics) => [...prevTopics, createdTopic]);
      setTemplate((prevTemplate) => ({
        ...prevTemplate,
        topic: createdTopic.id,
        subtopic: '',
      }));
      setAddTopic(false);
      setNewTopic('');
      onCreate();
    } catch (err) {
      showAlert('Failed to create topic', 'error');
    } finally {
      setTopicLoading(false);
    }
  };

  const handleCreateSubtopic = async () => {
    const trimmedName = newSubtopic.trim().toLowerCase();
    if (!trimmedName) return;

    const topicObj = topicsMap[template.topic];
    if (!topicObj) {
      showAlert('Cannot create subtopic without a selected topic.', 'error');
      return;
    }

    const existing = (topicObj.subtopics || []).find(
      (s) => s.name.trim().toLowerCase() === trimmedName
    );
    if (existing) {
      setTemplate((prev) => ({
        ...prev,
        subtopic: existing.id,
      }));
      setAddSubtopic(false);
      setNewSubtopic('');
      return;
    }

    setSubtopicLoading(true);
    try {
      const subtopicPayload = [
        {
          name: newSubtopic,
        },
      ];
      const response = await TemplatesApi.createSubtopic({
        topicId: topicObj.id,
        subtopics: subtopicPayload,
      });
      const createdSubtopic = response.data.data[0];
      setLocalTopics((prevTopics) =>
        prevTopics.map((topic) =>
          topic.id === topicObj.id
            ? {
                ...topic,
                subtopics: [...(topic.subtopics || []), createdSubtopic],
              }
            : topic
        )
      );
      setTemplate((prevTemplate) => ({
        ...prevTemplate,
        subtopic: createdSubtopic.id,
      }));
      setAddSubtopic(false);
      setNewSubtopic('');
      onCreate();
    } catch (err) {
      showAlert('Failed to create subtopic', 'error');
    } finally {
      setSubtopicLoading(false);
    }
  };

  const hasChanges = () => {
    const originalTemplate = { ...DEFAULT_TEMPLATE, ...initialTemplate };

    // Template content is different
    if (!_.isEqual(template, originalTemplate)) {
      return true;
    }

    // Check if a date was changed in a new template
    if (!initialTemplate) {
      return !!(startDate || endDate);
    }

    // Check if dates changed
    if (
      !!endDate !== !!initialTemplate.end_date ||
      (endDate && !endDate.isSame(initialTemplate.end_date, 'day')) ||
      !!startDate !== !!initialTemplate.start_date ||
      (startDate && !startDate.isSame(initialTemplate.start_date, 'day'))
    ) {
      return true;
    }

    return false;
  };

  const onClose = () => {
    if (!hasChanges()) {
      closeModal();
      return;
    }

    confirm({
      title: 'Are you sure you want to go back?',
      description: 'By closing this modal, you will lose your progress',
      confirmationText: 'Yes',
      cancellationText: 'No',
    })
      .then(closeModal)
      .catch(_.noop);
  };

  const handleDeleteTemplate = async () => {
    try {
      await confirm({
        title: 'Are you sure you want to end this template?',
        description: 'This will remove the template from view in the MLS app.',
        confirmationText: 'Yes',
        cancellationText: 'No',
      });

      // If the user confirms, proceed with deletion
      setLoading(true);
      await TemplatesApi.deleteTemplate(template.id);
      showAlert('The template has been successfully ended.', 'success');
      closeModal();
      onCreate(); // Trigger updates in the parent component
    } catch (err) {
      if (err === undefined || err === null) return;
      console.error('Error ending template:', err);
      showAlert('Failed to end template.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDateError = (key, errorCode) => {
    if (!errorCode) {
      setErrors(_.omit(errors, key));

      return;
    }

    let errorMsg;
    switch (errorCode) {
      case 'invalidDate':
        errorMsg = 'Please enter a valid date';
        break;

      case 'minDate':
        errorMsg =
          key === 'endDate'
            ? 'End date should be after start date'
            : 'Please enter a valid date'; // This only happens if the start date is before 1900
        break;

      default:
        errorMsg = '';
        break;
    }

    if (errorMsg) {
      setErrors({ ...errors, [key]: errorMsg });
    }
  };

  const renderError = (key) => {
    return (
      errors[key] && (
        <Typography variant="body2" color="red" paddingTop={1}>
          {errors[key]}
        </Typography>
      )
    );
  };

  const topicSelector = (
    <Box display="flex" flexDirection="row" gap={2.5}>
      <Box sx={styles.maxWidth}>
        <InputLabel style={{ fontWeight: 600, color: styleColors.base.black }}>
          Select Post Type
        </InputLabel>
        <Select
          fullWidth
          displayEmpty
          sx={styles.dropdown}
          value={template.category}
          onChange={handleCategoryChange}
          renderValue={(selected) =>
            TEMPLATE_CATEGORIES.find((c) => c.id === selected)?.name
          }
        >
          {TEMPLATE_CATEGORIES.map(({ id, name }) => (
            <MenuItem key={id} value={id}>
              {name}
            </MenuItem>
          ))}
        </Select>
        {renderError('category')}
      </Box>

      <Box sx={styles.maxWidth}>
        <InputLabel style={{ fontWeight: 600, color: styleColors.base.black }}>
          Topic
        </InputLabel>
        <Select
          fullWidth
          displayEmpty
          sx={styles.dropdown}
          value={isShoppableSelected || addTopic ? '' : template.topic || ''}
          disabled={isShoppableSelected || addTopic}
          onChange={(e) => {
            setTemplate((t) => ({
              ...t,
              topic: e.target.value,
              subtopic: '',
            }));
          }}
          onClose={() => {
            setAddTopic(false);
            setNewTopic('');
          }}
          renderValue={(selected) => {
            if (isShoppableSelected) return 'N/A for Product Links';
            if (addTopic) return 'Add Topic';
            return topicsMap[selected]?.name || 'No topic';
          }}
        >
          {localTopics.map(({ id, name }) => (
            <MenuItem key={id} value={id}>
              {name}
            </MenuItem>
          ))}
          {!isShoppableSelected && !addTopic && (
            <MenuItem
              style={{ fontWeight: 600 }}
              onClickCapture={(e) => {
                setAddTopic(true);
                e.stopPropagation();
              }}
            >
              + Add Topic
            </MenuItem>
          )}
          {addTopic && (
            <Box
              key="add-topic-box"
              display="flex"
              alignItems="center"
              gap={1}
              style={{
                paddingHorizontal: 15,
                paddingVertical: 6,
              }}
            >
              <TextField
                autoFocus
                value={newTopic || ''}
                onChange={(e) => setNewTopic(e.target.value)}
                variant="standard"
                size="small"
                style={{ marginLeft: 15 }}
                onKeyDown={
                  // prevents mui select from selecting items while the user writes
                  addTopic
                    ? (e) => {
                        e.stopPropagation();
                      }
                    : undefined
                }
                disabled={topicLoading}
                // prevents the select from closing when clicking when dropdown is empty
                onClickCapture={(e) => e.stopPropagation()}
              />
              <IconButton
                disabled={!newTopic || topicLoading}
                onClick={async () => {
                  await handleCreateTopic();
                }}
                color={newTopic ? 'success' : 'default'}
              >
                <CheckCircleOutline />
              </IconButton>
            </Box>
          )}
        </Select>
        {renderError('topic')}
      </Box>

      <Box sx={styles.maxWidth}>
        <InputLabel style={{ fontWeight: 600, color: styleColors.base.black }}>
          Subtopic
        </InputLabel>
        <Select
          disabled={isShoppableSelected || !template.topic || addSubtopic}
          fullWidth
          displayEmpty
          sx={styles.dropdown}
          value={
            isShoppableSelected || addSubtopic ? '' : template.subtopic || ''
          }
          onChange={(e) => {
            setTemplate((prevTemplate) => ({
              ...prevTemplate,
              subtopic: e.target.value,
            }));
          }}
          onClose={() => {
            setAddSubtopic(false);
            setNewSubtopic('');
          }}
          renderValue={(selected) => {
            if (isShoppableSelected) return 'N/A for Product Links';
            if (addSubtopic) return 'Add Subtopic';
            return (
              subtopics.find((subtopic) => subtopic.id === selected)?.name ||
              'No subtopic'
            );
          }}
        >
          {subtopics.map(({ id, name }) => (
            <MenuItem key={id} value={id}>
              {name}
            </MenuItem>
          ))}
          {!isShoppableSelected && !addSubtopic && template.topic && (
            <MenuItem
              style={{ fontWeight: 600 }}
              onClickCapture={(e) => {
                setAddSubtopic(true);
                e.stopPropagation();
              }}
            >
              + Add Subtopic
            </MenuItem>
          )}
          {addSubtopic && (
            <Box
              key="add-subtopic-box"
              display="flex"
              alignItems="center"
              gap={1}
              style={{
                paddingHorizontal: 15,
                paddingVertical: 6,
              }}
            >
              <TextField
                autoFocus
                value={newSubtopic || ''}
                onChange={(e) => setNewSubtopic(e.target.value)}
                variant="standard"
                size="small"
                style={{ marginLeft: 15 }}
                onKeyDown={
                  // prevents mui select from selecting items while the user writes
                  addSubtopic
                    ? (e) => {
                        e.stopPropagation();
                      }
                    : undefined
                }
                disabled={subtopicLoading}
                // prevets the select from closing when clicking when dropdown is empty
                onClickCapture={(e) => e.stopPropagation()}
              />
              <IconButton
                disabled={!newSubtopic || subtopicLoading}
                onClick={async () => {
                  await handleCreateSubtopic();
                }}
                color={newSubtopic ? 'success' : 'default'}
              >
                <CheckCircleOutline />
              </IconButton>
            </Box>
          )}
        </Select>
        {renderError('subtopic')}
      </Box>
    </Box>
  );
  const showEndNowButton = (() => {
    const currentEndDate = getInitialDate(initialTemplate?.end_date);
    return (
      initialTemplate &&
      ((!currentEndDate && evergreen) ||
        (currentEndDate && currentEndDate.isAfter(moment())))
    );
  })();
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Modal open sx={styles.modalWrapper}>
        <Box sx={styles.modalContent}>
          <Typography variant="h5" paddingBottom={4}>
            {initialTemplate ? 'Edit' : 'Create'} Template
          </Typography>

          <Box paddingBottom={1.5}>{topicSelector}</Box>

          <Box paddingBottom={3}>
            <TemplateContentForm
              template={template}
              onChange={setTemplate}
              renderError={renderError}
            />
          </Box>

          {/* TODO: Targeting */}

          <Stack
            direction="column"
            sx={{ width: '65%', paddingY: 2, marginX: 'auto' }}
          >
            <DatePicker
              required
              label="Start at midnight Central on"
              variant="filled"
              value={startDate}
              onError={(err) => handleDateError('startDate', err)}
              onChange={(event) => {
                if (!event) {
                  setStartDate(null);
                  return;
                }

                const centralDate = moment
                  .tz(event.format('YYYY-MM-DD'), 'America/Chicago')
                  .startOf('day');

                setStartDate(centralDate);
                handleDateError('startDate', null);
              }}
            />
            {renderError('startDate')}

            <Stack
              direction="row"
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginTop: 2,
              }}
            >
              <DatePicker
                label="End at 11:59 Central on"
                variant="filled"
                minDate={evergreen ? null : startDate}
                disabled={evergreen}
                value={endDate}
                onError={(err) => handleDateError('endDate', err)}
                onChange={(event) => {
                  if (!event) {
                    setEndDate(null);
                    return;
                  }

                  const centralDate = moment
                    .tz(event.format('YYYY-MM-DD'), 'America/Chicago')
                    .endOf('day');

                  setEndDate(centralDate);
                  handleDateError('endDate', null);
                }}
              />

              <Typography sx={{ alignSelf: 'center' }}>OR</Typography>

              <Button
                sx={[
                  { height: 56 },
                  evergreen
                    ? styles.evergreenSelected
                    : styles.evergreenUnselected,
                ]}
                onClick={() => setEvergreen(!evergreen)}
              >
                Evergreen
              </Button>
            </Stack>

            {!evergreen && renderError('endDate')}
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" paddingTop={2}>
              <SpinningSpark marginTop="0" />
            </Box>
          ) : (
            <Box display="flex" justifyContent="space-between" paddingTop={2}>
              <Box minWidth={160}>
                {showEndNowButton && (
                  <Button
                    variant="text"
                    sx={styles.deleteButton}
                    onClick={handleDeleteTemplate}
                    startIcon={<Delete />}
                  >
                    End Now
                  </Button>
                )}
              </Box>
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  color="secondary"
                  style={{
                    ...styles.footerBtn,
                    backgroundColor: styleColors.base.inactive,
                    color: styleColors.base.black,
                  }}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  style={{
                    ...styles.footerBtn,
                    backgroundColor: styleColors.base.walmart_blue,
                  }}
                  onClick={onSubmit}
                >
                  {initialTemplate ? 'Update' : 'Create'} Template
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>
    </LocalizationProvider>
  );
}

const styles = {
  modalWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  modalContent: {
    overflow: 'scroll',
    maxHeight: '95vh',
    bgcolor: 'white',
    width: '683px',
    padding: 4,
    borderRadius: 4,
    boxShadow: 24,
  },
  inputLabel: {
    fontWeight: 600,
    color: styleColors.base.black,
  },
  dropdown: {
    flex: 1,
    height: 55,
    minWidth: 150,
    maxWidth: 250,
    backgroundColor: '#F6F9FC',
    borderRadius: 2,
    '& fieldset': { border: 'none' },
  },
  datePicker: {
    borderRadius: 2,
    width: '100%',
    backgroundColor: '#F6F9FC',
    '& fieldset': { border: 'none' },
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  footerBtn: {
    minHeight: '50px',
    borderRadius: 10,
    fontWeight: 600,
  },
  maxWidth: {
    width: '100%',
  },
  evergreenUnselected: {
    backgroundColor: styleColors.base.almost_white_blue,
    border: `1px solid ${styleColors.base.almost_white_blue}`,
    '&:hover': {
      backgroundColor: styleColors.base.light_green,
      border: `1px solid ${styleColors.base.green}`,
    },
  },
  evergreenSelected: {
    backgroundColor: styleColors.base.green,
    border: `1px solid ${styleColors.base.green}`,
    '&:hover': {
      backgroundColor: styleColors.base.green,
      border: `1px solid ${styleColors.base.green}`,
    },
  },
  deleteButton: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 16,
    color: 'black',
    '&:hover': {
      backgroundColor: 'transparent',
      color: 'red',
      textDecoration: 'none',
    },
    '&:focus': {
      textDecoration: 'none',
    },
    '&:active': {
      textDecoration: 'none',
    },
  },
};

TemplateCreateModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  topics: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  initialTemplate: PropTypes.shape({
    id: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
  }),
};

TemplateCreateModal.defaultProps = {
  initialTemplate: null,
};
